import { useEffect, useMemo, useState } from 'react';

import { collection, onSnapshot } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type { ShiftListItem } from '@src/packages/shifts/types';

type PoliciesState = Record<string, unknown[]>;

export const useShiftPoliciesSubcollections = (
  orgId: string,
  parentDocs: ShiftListItem[],
): ShiftListItem[] => {
  const [policiesState, setPoliciesState] = useState<PoliciesState>({});

  const parentDocIds = useMemo(
    () =>
      parentDocs
        .map(doc => doc.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
        .sort()
        .join(','),
    [parentDocs],
  );

  useEffect(() => {
    if (!orgId || !parentDocIds) {
      setPoliciesState({});
      return undefined;
    }

    const docIds = parentDocIds.split(',').filter(Boolean);
    const unsubs: Array<() => void> = [];

    docIds.forEach(docId => {
      const policiesRef = collection(
        db,
        FSCollections.ORGANIZATIONS,
        orgId,
        FSCollections.WORK_SHIFTS,
        docId,
        FSCollections.POLICIES,
      );

      const unsub = onSnapshot(policiesRef, snapshot => {
        const policies = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPoliciesState(prev => ({
          ...prev,
          [docId]: policies,
        }));
      });

      unsubs.push(unsub);
    });

    return () => {
      unsubs.forEach(unsub => unsub());
    };
  }, [orgId, parentDocIds]);

  return useMemo(
    () =>
      parentDocs.map(doc => {
        const docId = doc.id;
        if (!docId) return doc;

        const policies = policiesState[docId];
        const resolvedPolicies = policies ?? doc.policies ?? [];

        return {
          ...doc,
          policies: resolvedPolicies,
          exceptional: resolvedPolicies.length > 0,
          cachedPolicies: policies ?? doc.cachedPolicies ?? null,
        };
      }),
    [parentDocs, policiesState],
  );
};
